import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Grid, Card, CardContent, Typography, Box, LinearProgress, Tooltip, Divider } from "@mui/material";
import { Map, RoundaboutLeft, Assessment, Straighten, Business, ViewModule, Warehouse, PieChart as PieChartIcon, LocationCity } from "@mui/icons-material";
import { IPDCWelcomeMessage } from "../../../../components";
import ViewMessageList from "../../Shared/Message/ViewMessageList";
import { infraImg, cadastreImg } from "../../../../assets/image/dashboard";
import { WideChartContainer } from "../../../../components/Charts";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests"

// Helper function to convert any value to number
const toNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
  if (typeof value === 'object' && value !== null) {
    const num = parseFloat(value.toString());
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  const num = toNumber(number);
  if (num === 0) return "0";
  return new Intl.NumberFormat('en-US').format(num);
};

// Format percentage
const formatPercentage = (value) => {
  const num = toNumber(value);
  if (num === 0) return "0%";
  return `${num.toFixed(1)}%`;
};

// Get color based on percentage value
const getProgressColor = (value) => {
  const num = toNumber(value);
  if (num >= 80) return "#4caf50";
  if (num >= 60) return "#8bc34a";
  if (num >= 40) return "#ffc107";
  if (num >= 20) return "#ff9800";
  return "#f44336";
};

// Progress Bar Component for cells
const ProgressBarCell = ({ value, label, actualValue, color }) => {
  const percentage = Math.min(100, Math.max(0, toNumber(value)));
  const barColor = color || getProgressColor(percentage);
  
  return (
    <Box sx={{ minWidth: 120 }}>
      <Tooltip title={`${label}: ${formatPercentage(percentage)}`} arrow>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.85rem', color: barColor, fontWeight: 'bold' }}>
              {formatPercentage(percentage)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                borderRadius: 5,
              }
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

// Land Use Pie Chart Component - NOT CLICKABLE
const LandUsePieChart = ({ data }) => {
  const total = Object.values(data).reduce((sum, val) => sum + toNumber(val), 0);
  const colors = ['#1976d2', '#4caf50', '#795548'];
  
  if (total === 0) {
    return (
      <Card 
        sx={{ 
          bgcolor: '#E3F2FD',
          p: 3, 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center'
        }}
      >
        <Typography variant="body1" color="textSecondary" sx={{ fontSize: '1rem' }}>No land use data available</Typography>
      </Card>
    );
  }
  
  let startAngle = 0;
  const centerX = 90, centerY = 90, radius = 70;
  const svgSize = 180;
  
  const items = Object.keys(data).filter(key => toNumber(data[key]) > 0);
  
  return (
    <Card 
      sx={{ 
        bgcolor: '#E3F2FD',
        p: 2, 
        height: '100%'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', textAlign: 'center', mb: 1 }}>
        Land Use Proportion
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', display: 'block', mb: 1.5 }}>
        Total: {formatNumber(total)} m²
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, pointerEvents: 'none' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = toNumber(data[key]);
              const percentage = (value / total) * 100;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ transition: 'opacity 0.3s' }}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            <circle cx={centerX} cy={centerY} r="35" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 10} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1976d2">
              {(total / 10000).toFixed(1)}ha
            </text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 110 }}>
          {items.map((key, index) => {
            const value = toNumber(data[key]);
            const percentage = (value / total) * 100;
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', flex: 1 }}>
                  {key}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', color: colors[index % colors.length] }}>
                  {percentage.toFixed(0)}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

// Park Leased Land Occupancy Card Component - NOT CLICKABLE
const ParkLeasedLandOccupancyCard = ({ park, totalLeasedLandArea, occupiedLeasedLandArea, occupancyRate }) => {
  const blueColor = "#1976d2";
  const getColorByRate = (rate) => {
    if (rate >= 80) return "#4caf50";
    if (rate >= 60) return "#8bc34a";
    if (rate >= 40) return "#ffc107";
    if (rate >= 20) return "#ff9800";
    return "#f44336";
  };
  
  const rateColor = getColorByRate(occupancyRate);
  
  return (
    <Card 
      sx={{ 
        p: 3, 
        bgcolor: '#E3F2FD',
        boxShadow: 3, 
        height: '100%', 
        width: '100%'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: blueColor, fontSize: '1.2rem' }}>
            {park.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem', display: 'block', mt: 0.5 }}>
            ID: {park.id}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: `${rateColor}20`, borderRadius: '50%', p: 1.5 }}>
          <LocationCity sx={{ fontSize: 40, color: rateColor }} />
        </Box>
      </Box>
      
      <Divider sx={{ my: 1.5 }} />
      
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', color: '#666', fontWeight: 600 }}>
            Leased Land Occupancy Rate
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', color: rateColor, fontWeight: 'bold' }}>
            {formatPercentage(occupancyRate)}
          </Typography>
        </Box>
        
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, occupancyRate))}
          sx={{
            height: 12,
            borderRadius: 6,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: rateColor,
              borderRadius: 6,
            }
          }}
        />
        
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                Occupied Leased Land
              </Typography>
              <Typography variant="h6" fontWeight="bold" color={rateColor} sx={{ fontSize: '1rem', mt: 0.5 }}>
                {formatNumber(occupiedLeasedLandArea)} m²
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                Total Leased Land
              </Typography>
              <Typography variant="h6" fontWeight="bold" color={blueColor} sx={{ fontSize: '1rem', mt: 0.5 }}>
                {formatNumber(totalLeasedLandArea)} m²
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem', display: 'block', textAlign: 'center', fontWeight: 500 }}>
            Vacant Leased Land: {formatNumber(totalLeasedLandArea - occupiedLeasedLandArea)} m²
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center', mt: 0.5 }}>
            {( (totalLeasedLandArea - occupiedLeasedLandArea) / 10000).toFixed(2)} ha
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default function GISDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [parksData, setParksData] = useState([]);
  const [parcelsData, setParcelsData] = useState([]);
  const [buildingsData, setBuildingsData] = useState([]);
  const [shedsData, setShedsData] = useState([]);
  const [greenAreasData, setGreenAreasData] = useState([]);
  const [roadsData, setRoadsData] = useState([]);
  const [landLeasedData, setLandLeasedData] = useState([]);
  
  // State for park leased land occupancy metrics
  const [parkLeasedLandMetrics, setParkLeasedLandMetrics] = useState([]);
  
  // State for calculated metrics
  const [summaryStats, setSummaryStats] = useState({
    occupancyRate: 0,
    parcelRatio: 0,
    totalParkArea: 0,
    totalOccupiedArea: 0,
    totalParcels: 0,
    totalOccupiedParcels: 0,
    totalParks: 0,
    totalSheds: 0,
    totalBuildings: 0,
    buildingUtilization: 0,
    shedOccupancy: 0,
    totalActiveBuildings: 0,
    totalOccupiedSheds: 0
  });
  
  // State for land use data
  const [landUseData, setLandUseData] = useState({
    'Parcels': 0,
    'Green Areas': 0,
    'Roads': 0
  });

  const blueColor = "#1976d2";
  const brownColor = "#795548";
  const greenColor = "#4caf50";
  const lightBlueBg = "#e3f2fd";
  const lightGrayBg = "#f5f5f5";

  const getBuildingsForPark = useCallback((parkId) => {
    return buildingsData.filter(building => toNumber(building.park_id) === toNumber(parkId));
  }, [buildingsData]);

  const calculateGreenAreaTotal = useCallback((parkId) => {
    if (!greenAreasData || !Array.isArray(greenAreasData)) return 0;
    return greenAreasData
      .filter(item => toNumber(item.park_id) === toNumber(parkId))
      .reduce((sum, item) => {
        let area = toNumber(item.capacity || item.area || 0);
        return sum + area;
      }, 0);
  }, [greenAreasData]);

  const calculateRoadAreaTotal = useCallback((parkId) => {
    if (!roadsData || !Array.isArray(roadsData)) return 0;
    return roadsData
      .filter(item => toNumber(item.park_id) === toNumber(parkId))
      .reduce((sum, item) => {
        let area = toNumber(item.capacity || item.area || 0);
        if (area === 0 && item.width && item.geom) {
          area = toNumber(item.width) * 100;
        }
        return sum + area;
      }, 0);
  }, [roadsData]);

  const parkMetrics = useMemo(() => {
    const metrics = {};
    parksData.forEach(park => {
      const parkId = park.id;
      const parkArea = toNumber(park.area_on_plan || 0);
      const parkAreaM2 = parkArea;
      
      const parkParcels = parcelsData.filter(p => toNumber(p.park_id) === toNumber(parkId));
      
      let totalParcelArea = 0;
      parkParcels.forEach(parcel => {
        totalParcelArea += toNumber(parcel.area || 0);
      });
      
      const greenArea = calculateGreenAreaTotal(parkId);
      const roadArea = calculateRoadAreaTotal(parkId);
      const parkBuildings = getBuildingsForPark(parkId);
      const totalBuildingArea = parkBuildings.reduce((sum, b) => sum + toNumber(b.floor_area_m2), 0);
      const parkSheds = shedsData.filter(s => toNumber(s.park_id) === toNumber(parkId));
      const totalShedArea = parkSheds.reduce((sum, s) => sum + toNumber(s.shed_size), 0);
      
      const occupiedArea = parkParcels
        .filter(p => p.occupancy_status === "Occupied" || p.status === "Leased")
        .reduce((sum, p) => sum + toNumber(p.area), 0);
      
      const occupiedParcels = parkParcels.filter(p => p.occupancy_status === "Occupied" || p.status === "Leased").length;
      
      const occupancyRate = parkAreaM2 > 0 ? (occupiedArea / parkAreaM2) * 100 : 0;
      const parcelRatio = parkParcels.length > 0 ? (occupiedParcels / parkParcels.length) * 100 : 0;
      
      const activeBuildings = parkBuildings.filter(b => b.status === "Active").length;
      const buildingRatio = parkBuildings.length > 0 ? (activeBuildings / parkBuildings.length) * 100 : 0;
      
      const occupiedSheds = parkSheds.filter(s => s.status_utilization === "Occupied").length;
      const shedOccupancyRate = parkSheds.length > 0 ? (occupiedSheds / parkSheds.length) * 100 : 0;
      
      const greenAreaRatio = parkAreaM2 > 0 ? (greenArea / parkAreaM2) * 100 : 0;
      const roadRatio = parkAreaM2 > 0 ? (roadArea / parkAreaM2) * 100 : 0;
      
      metrics[park.name] = {
        park,
        totalArea: parkArea,
        totalAreaM2: parkAreaM2,
        totalParcelArea,
        occupiedArea,
        occupancyRate,
        totalParcels: parkParcels.length,
        occupiedParcels,
        parcelRatio,
        totalBuildings: parkBuildings.length,
        activeBuildings,
        buildingRatio,
        totalSheds: parkSheds.length,
        occupiedSheds,
        shedOccupancyRate,
        greenAreaRatio,
        roadRatio,
        totalBuildingArea,
        totalShedArea,
        greenArea,
        roadArea
      };
    });
    return metrics;
  }, [parksData, parcelsData, getBuildingsForPark, shedsData, calculateGreenAreaTotal, calculateRoadAreaTotal]);

  const calculateSummaryStats = useCallback(() => {
    let totalParkArea = 0;
    let totalOccupiedArea = 0;
    let totalParcels = 0;
    let totalOccupiedParcels = 0;
    let totalSheds = 0;
    let totalBuildings = 0;
    let totalActiveBuildings = 0;
    let totalOccupiedSheds = 0;
    let totalParcelArea = 0;
    let totalGreenArea = 0;
    let totalRoadArea = 0;
    
    Object.values(parkMetrics).forEach(metric => {
      totalParkArea += metric.totalAreaM2;
      totalOccupiedArea += metric.occupiedArea;
      totalParcels += metric.totalParcels;
      totalOccupiedParcels += metric.occupiedParcels;
      totalSheds += metric.totalSheds;
      totalBuildings += metric.totalBuildings;
      totalActiveBuildings += metric.activeBuildings;
      totalOccupiedSheds += metric.occupiedSheds;
      totalParcelArea += metric.totalParcelArea || 0;
      totalGreenArea += metric.greenArea || 0;
      totalRoadArea += metric.roadArea || 0;
    });
    
    const overallOccupancyRate = totalParkArea > 0 ? (totalOccupiedArea / totalParkArea) * 100 : 0;
    const overallParcelRatio = totalParcels > 0 ? (totalOccupiedParcels / totalParcels) * 100 : 0;
    const overallBuildingUtilization = totalBuildings > 0 ? (totalActiveBuildings / totalBuildings) * 100 : 0;
    const overallShedOccupancy = totalSheds > 0 ? (totalOccupiedSheds / totalSheds) * 100 : 0;
    
    setSummaryStats({
      occupancyRate: overallOccupancyRate,
      parcelRatio: overallParcelRatio,
      totalParkArea,
      totalOccupiedArea,
      totalParcels,
      totalOccupiedParcels,
      totalParks: parksData.length,
      totalSheds: totalSheds,
      totalBuildings: totalBuildings,
      buildingUtilization: overallBuildingUtilization,
      shedOccupancy: overallShedOccupancy,
      totalActiveBuildings: totalActiveBuildings,
      totalOccupiedSheds: totalOccupiedSheds
    });
    
    setLandUseData({
      'Parcels': totalParcelArea,
      'Green Areas': totalGreenArea,
      'Roads': totalRoadArea
    });
  }, [parkMetrics, parksData.length]);

  // Calculate park leased land occupancy metrics
  const calculateParkLeasedLandMetrics = useCallback(() => {
    if (!landLeasedData || landLeasedData.length === 0 || parcelsData.length === 0 || parksData.length === 0) {
      setParkLeasedLandMetrics([]);
      return;
    }

    const parkMetricsArray = [];
    
    parksData.forEach(park => {
      const parkId = park.id;
      
      // Get all parcels for this park
      const parkParcels = parcelsData.filter(p => toNumber(p.park_id) === toNumber(parkId));
      
      // Filter to get only leased parcels (those in landLeasedData)
      const leasedParcels = parkParcels.filter(parcel => 
        landLeasedData.some(leased => leased.upin === parcel.upin)
      );
      
      // Calculate total leased land area for this park
      const totalLeasedLandArea = leasedParcels.reduce((sum, parcel) => sum + toNumber(parcel.area || 0), 0);
      
      // Calculate occupied leased land area (parcels that are both leased AND occupied)
      const occupiedLeasedParcels = leasedParcels.filter(parcel => 
        parcel.occupancy_status === "Occupied" || parcel.status === "Leased"
      );
      
      const occupiedLeasedLandArea = occupiedLeasedParcels.reduce((sum, parcel) => sum + toNumber(parcel.area || 0), 0);
      
      // Calculate occupancy rate for leased land
      const leasedLandOccupancyRate = totalLeasedLandArea > 0 ? (occupiedLeasedLandArea / totalLeasedLandArea) * 100 : 0;
      
      if (totalLeasedLandArea > 0) {
        parkMetricsArray.push({
          park: { id: park.id, name: park.name },
          totalLeasedLandArea,
          occupiedLeasedLandArea,
          leasedLandOccupancyRate,
          numberOfLeasedParcels: leasedParcels.length,
          numberOfOccupiedLeasedParcels: occupiedLeasedParcels.length
        });
      }
    });
    
    // Sort by leased land occupancy rate (descending)
    parkMetricsArray.sort((a, b) => b.leasedLandOccupancyRate - a.leasedLandOccupancyRate);
    
    console.log("Park Leased Land Metrics:", parkMetricsArray);
    setParkLeasedLandMetrics(parkMetricsArray);
  }, [landLeasedData, parcelsData, parksData]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [parksummary, parcelsummary, buildingsummary, shedsummary, greenAreaData, roadData, leasedland] = await Promise.all([
          GetDataFromApiWithParams(`parks/parksummary`),
          GetDataFromApiWithParams(`parks/parcelsummary`),
          GetDataFromApiWithParams(`parks/buildingsummary`),
          GetDataFromApiWithParams(`parks/shedsummary`),
          GetDataFromApiWithParams(`parks/greenareasummary`),
          GetDataFromApiWithParams(`parks/roadsummary`),
          GetDataFromApiWithParams(`parks/landleasedsummary`)
        ]);
        
        setParksData(parksummary || []);
        setParcelsData(parcelsummary || []);
        setGreenAreasData(greenAreaData || []);
        setRoadsData(roadData || []);
        setLandLeasedData(leasedland || []);
        
        const processedBuildings = (buildingsummary || []).map(building => {
          let floorArea = toNumber(building.floor_area || building.area || building.building_area || 0);
          if (floorArea === 0 && building.no_of_floors) {
            floorArea = toNumber(building.no_of_floors) * 500;
          }
          return {
            id: building.id,
            park_id: parseInt(building.park_id),
            status: building.status || "Active",
            name: building.name || `Building ${building.id}`,
            bldg_no: building.bldg_no || building.block_id || `B-${building.id}`,
            no_of_floors: toNumber(building.no_of_floors || 1),
            no_of_units: toNumber(building.no_of_units || 0),
            floor_area_m2: floorArea,
            occupancy_ratio: toNumber(building.occupancy_ratio || building.occupancy_rate || 0)
          };
        });
        
        const processedSheds = (shedsummary || []).map(shed => ({
          id: shed.id,
          park_id: toNumber(shed.park_id),
          upin: shed.upin || null,
          status_utilization: shed.status_utilization || "Vacant",
          shed_size: toNumber(shed.shed_size || shed.area || 0)
        }));
        
        setBuildingsData(processedBuildings);
        setShedsData(processedSheds);
        
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(parkMetrics).length > 0) {
      calculateSummaryStats();
    }
  }, [parkMetrics, calculateSummaryStats]);

  useEffect(() => {
    if (landLeasedData.length > 0 && parcelsData.length > 0 && parksData.length > 0) {
      calculateParkLeasedLandMetrics();
    }
  }, [landLeasedData, parcelsData, parksData, calculateParkLeasedLandMetrics]);

  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        
        
        {/* Park Leased Land Occupancy Cards Section */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2, mt: 1 }}>
            Park Leased Land Occupancy Rates
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3, fontSize: '1rem' }}>
            Occupancy rate of leased land for each park (occupied leased land area / total leased land area)
          </Typography>
          
          <Grid container spacing={3}>
            {parkLeasedLandMetrics.map((parkMetric) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={parkMetric.park.id}>
                <ParkLeasedLandOccupancyCard
                  park={parkMetric.park}
                  totalLeasedLandArea={parkMetric.totalLeasedLandArea}
                  occupiedLeasedLandArea={parkMetric.occupiedLeasedLandArea}
                  occupancyRate={parkMetric.leasedLandOccupancyRate}
                />
              </Grid>
            ))}
          </Grid>
          
          {parkLeasedLandMetrics.length === 0 && (
            <Card sx={{ p: 4, bgcolor: lightGrayBg, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                No leased land data available for any park
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}